import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import loadingGif from '../../assets/loading.gif';

interface RoleProtectedRouteProps {
  children: React.ReactElement;
  allowedRoles: string[];
  redirectTo?: string;
}

/**
 * Role-based route protection component
 * Ensures only users with specific roles can access certain routes
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo = '/submissions',
}) => {
  const { user, isAuthenticated, loading } = useAuth();

  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#000000'
      }}>
        <div className="loading-container" style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: '60px'
        }}>
          <img src={loadingGif} alt="Loading..." className="loading-gif" />
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  // If user doesn't have required role, redirect to default page
  // Convert to lowercase for case-insensitive comparison
  const userRoleLower = user.role?.toLowerCase();
  const allowedRolesLower = allowedRoles.map(role => role.toLowerCase());

  if (!allowedRolesLower.includes(userRoleLower)) {
    console.warn(`Access denied: User role '${user.role}' not in allowed roles:`, allowedRoles);
    return (
      <Navigate
        to={redirectTo}
        replace
      />
    );
  }

  // User has required role, render the protected content
  return children;
};

export default RoleProtectedRoute;
